package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.ConcurrentMutableMap
import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentitySet
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.jvm.JvmStatic

/**
 * 反应式可变 Map（路线 B）。
 *
 * <p>语义：key 级粒度。{@code map.get("name")} 仅订阅 "name" 这一个 key，
 * 后续 {@code put("name", ...)} 才通知；{@code put("other", ...)} 不通知 name 的订阅者。
 * 与 Compose {@code SnapshotStateMap} 对齐，追求"读啥订阅啥"的最小通知集。</p>
 *
 * <p>结构级订阅：{@code size() / isEmpty() / keySet() / entrySet() / containsKey()}
 * 订阅"结构 Trackable"——任何 key 的新增 / 删除都通知结构订阅者；纯改值（key 已存在）不通知结构。</p>
 *
 * <p>实现 {@link Map}{@code <String, Any>} 接口契约。DSL 中所有 map key 都是 String，
 * 这里把这一约定固化到泛型上；非 String key 的 put 会在内部转 String（与现有
 * {@code IndexAssign} 行为一致）。</p>
 *
 * <p>线程安全：subscribers 集合用 [SyncLock] 守护（等价迁移前的 synchronized(this)）；keyTrackables 用
 * [ConcurrentMutableMap]（JVM 端为 ConcurrentHashMap）支持懒创建。读路径不持锁。</p>
 */
@Suppress("CyclomaticComplexMethod", "LongMethod", "TooManyFunctions")
class ReactiveMap(private val backing: LinkedHashMap<String, Any> = LinkedHashMap()) :
    MutableMap<String, Any>,
    Trackable {

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.reactive.ReactiveMap")

        /** 空 map 构造。 */
        @JvmStatic
        fun empty(): ReactiveMap = ReactiveMap()
    }

    /** 每个 key 一个独立 Trackable，懒创建。订阅者通过 {@link ReactiveContext#trackKey} 自动注册到对应实例。 */
    private val keyTrackables: ConcurrentMutableMap<String, KeyTrackable> = ConcurrentMutableMap()

    /** 结构级 Trackable：监听 size / keySet / entrySet / containsKey 等"非具体 value"的访问。 */
    private val structural = StructuralTrackable()

    /** Trackable 订阅入口互斥锁（等价迁移前的 this 监视器）。 */
    private val lock = SyncLock()

    /** 用现有 Map 构造；浅拷贝以避免外部继续 mutate 原 map 时绕过通知。 */
    constructor(source: Map<*, *>) : this(LinkedHashMap(source.size)) {
        for ((key, value) in source) {
            if (value != null) backing[key.toString()] = value
        }
    }

    // ===== 内部 Trackable =====

    /** 单 key 的 Trackable 实现。 */
    private class KeyTrackable : Trackable {
        /** subs 集合互斥锁（等价迁移前的 this 监视器）。 */
        private val lock = SyncLock()
        private val subs: MutableSet<Subscriber> = newIdentitySet()

        override fun subscribe(subscriber: Subscriber) {
            lock.withLock {
                subs.add(subscriber)
            }
        }

        override fun unsubscribe(subscriber: Subscriber) {
            lock.withLock {
                subs.remove(subscriber)
            }
        }

        fun snapshot(): List<Subscriber> {
            lock.withLock {
                return ArrayList(subs)
            }
        }
    }

    /** 结构级 Trackable，与 KeyTrackable 同形态但语义独立。 */
    private class StructuralTrackable : Trackable {
        /** subs 集合互斥锁（等价迁移前的 this 监视器）。 */
        private val lock = SyncLock()
        private val subs: MutableSet<Subscriber> = newIdentitySet()

        override fun subscribe(subscriber: Subscriber) {
            lock.withLock {
                subs.add(subscriber)
            }
        }

        override fun unsubscribe(subscriber: Subscriber) {
            lock.withLock {
                subs.remove(subscriber)
            }
        }

        fun snapshot(): List<Subscriber> {
            lock.withLock {
                return ArrayList(subs)
            }
        }
    }

    /** 取 / 建指定 key 的 Trackable，供 ReactiveContext 注册订阅。 */
    internal fun trackableFor(key: String): Trackable {
        val existing = keyTrackables[key]
        if (existing != null) return existing
        val created = KeyTrackable()
        val winner = keyTrackables.putIfAbsent(key, created)
        return winner ?: created
    }

    /** 暴露结构级 Trackable，供 size/keys 等读路径登记订阅。 */
    internal fun structuralTrackable(): Trackable = structural

    private fun notifyKey(key: String) {
        val t = keyTrackables[key] ?: return
        val snapshot = t.snapshot()
        if (BatchScope.inBatch()) {
            BatchScope.enqueue(snapshot)
            return
        }
        for (s in snapshot) {
            s.onChange()
        }
    }

    private fun notifyStructural() {
        val snapshot = structural.snapshot()
        if (BatchScope.inBatch()) {
            BatchScope.enqueue(snapshot)
            return
        }
        for (s in snapshot) {
            s.onChange()
        }
    }

    private fun trackStructural() {
        ReactiveContext.track(structural)
    }

    // ===== Trackable =====

    override fun subscribe(subscriber: Subscriber) {
        lock.withLock {
            structural.subscribe(subscriber)
        }
    }

    override fun unsubscribe(subscriber: Subscriber) {
        lock.withLock {
            structural.unsubscribe(subscriber)
        }
    }

    // ===== Map 读 =====

    override val size: Int
        get() {
            trackStructural()
            return backing.size
        }

    override val keys: MutableSet<String>
        get() {
            trackStructural()
            // 快照副本本就与 backing 脱钩；common 无 Collections.unmodifiableSet，
            // 直接返回副本（mutate 副本不影响本反应式 Map，仅不再抛 UnsupportedOperationException）
            return LinkedHashMap(backing).keys
        }

    override val values: MutableCollection<Any>
        get() {
            trackStructural()
            // 同 keys：快照副本直接返回（common 无 Collections.unmodifiableCollection）
            return ArrayList(backing.values)
        }

    override val entries: MutableSet<MutableMap.MutableEntry<String, Any>>
        get() {
            trackStructural()
            // 同 keys：快照副本直接返回（common 无 Collections.unmodifiableSet）
            return LinkedHashMap(backing).entries
        }

    override fun isEmpty(): Boolean {
        trackStructural()
        return backing.isEmpty()
    }

    override fun containsKey(key: String): Boolean {
        // containsKey 介于 key 级与结构级之间：先按 key 订阅（key 存在状态本身是 key 的属性），
        // 同时也订阅结构（key 增删时必须通知）。这里只订阅 key 级，put 已存在 key 不会改变 containsKey 结果。
        // 但删除该 key 必须通知 → notifyKey 在 remove 路径会被调用，OK。
        ReactiveContext.track(trackableFor(key))
        return backing.containsKey(key)
    }

    override fun containsValue(value: Any): Boolean {
        // 全量值扫描 → 订阅结构（粗粒度兜底，containsValue 本就少用）
        trackStructural()
        return backing.containsValue(value)
    }

    override fun get(key: String): Any? {
        ReactiveContext.track(trackableFor(key))
        return backing[key]
    }

    // ===== Map 写 =====

    override fun put(key: String, value: Any): Any? {
        val isNew = !backing.containsKey(key)
        val old = backing.put(key, value)
        if (old != value) {
            notifyKey(key)
        }
        if (isNew) {
            notifyStructural()
        }
        return old
    }

    override fun remove(key: String): Any? {
        if (!backing.containsKey(key)) {
            return null
        }
        val old = backing.remove(key)
        notifyKey(key)
        notifyStructural()
        return old
    }

    override fun putAll(from: Map<out String, Any>) {
        for ((key, value) in from) {
            put(key, value)
        }
    }

    override fun clear() {
        if (backing.isEmpty()) return
        // 通知所有现有 key 的订阅者：值已经"消失"
        val keys = ArrayList(backing.keys)
        backing.clear()
        for (k in keys) {
            notifyKey(k)
        }
        notifyStructural()
    }

    // ===== equals / hashCode / toString =====

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        // 不 track —— equals 通常用于断言或外部比较，不应建立反应式依赖
        if (other !is Map<*, *>) return false
        return backing == other
    }

    override fun hashCode(): Int = backing.hashCode()

    override fun toString(): String = backing.toString()
}
